/**
 * Axios 实例 + JWT 拦截器 + 错误处理
 */
import axios, { type AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import type { ApiResponse } from './types'

// ── Axios 实例 ──
const client = axios.create({
  baseURL: '/api/v1',
  timeout: 15000,
})

// ── 请求拦截器:自动添加 Bearer token ──
client.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 从 localStorage 读取 token (Zustand persist)
    const raw = localStorage.getItem('mailman-auth')
    if (raw) {
      try {
        const parsed = JSON.parse(raw)
        const token = parsed?.state?.accessToken
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
      } catch {
        // ignore parse error
      }
    }
    return config
  },
  (error) => Promise.reject(error),
)

// ── API 错误类型 ──
export class ApiError extends Error {
  status: number
  message: string

  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.message = message
  }
}

// ── 响应拦截器:统一错误处理 ──
client.interceptors.response.use(
  (response) => {
    const data = response.data as ApiResponse<unknown>
    // 后端返回 success: false 时包装为错误
    if (data && data.success === false) {
      const msg = data.error || '请求失败'
      return Promise.reject(new ApiError(response.status, msg))
    }
    return response
  },
  (error: AxiosError<ApiResponse<unknown>>) => {
    // 网络错误
    if (!error.response) {
      return Promise.reject(new ApiError(0, '网络错误，无法连接到服务器'))
    }

    const status = error.response.status
    const data = error.response.data

    // 401 未授权 — 清除 token 并跳转登录
    if (status === 401) {
      localStorage.removeItem('mailman-auth')
      if (window.location.pathname !== '/login') {
        window.location.href = '/login'
      }
    }

    const msg = data?.error || explainError(status)
    return Promise.reject(new ApiError(status, msg))
  },
)

// ── 错误码映射为中文 ──
export function explainError(status: number): string {
  switch (status) {
    case 400: return '请求参数错误'
    case 401: return '未登录或登录已过期'
    case 403: return '没有权限执行此操作'
    case 404: return '请求的资源不存在'
    case 409: return '资源冲突'
    case 429: return '请求过于频繁，请稍后重试'
    case 500: return '服务器内部错误'
    case 502: return '网关错误'
    case 503: return '服务不可用'
    case 504: return '网关超时'
    default: return `请求失败 (${status})`
  }
}

// ── 提取 data ──
export async function unwrap<T>(
  promise: Promise<AxiosResponse<ApiResponse<T>>>,
): Promise<T> {
  const response = await promise
  const body = response.data
  if (body.success && body.data !== null) {
    return body.data
  }
  throw new ApiError(0, body.error || '响应数据为空')
}

export default client
